// src/components/OPDRenewals.js
import React, { useEffect, useMemo, useState } from "react";
import { Search, RefreshCw, AlertCircle, CheckCircle2, Loader2, Download, X, FilePenLine } from "lucide-react";
import MemberForm from "./MemberForm";

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';
const ATTACH_BASE_URL = API_BASE_URL.replace(/\/api$/, '') + '/self_registered_attachments';

export default function OPDRenewals() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [daysLeft, setDaysLeft] = useState("");
  const [submittingId, setSubmittingId] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [detailMember, setDetailMember] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [opdSubCity, setOpdSubCity] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const currentUserEmail = currentUser?.email;

  const fetchMembers = async () => {
    setLoading(true);
    try {
      if (!currentUserEmail) throw new Error('Not authenticated');
      // Fetch OPD profile to get sub city
      let subCity = opdSubCity;
      if (!subCity) {
        const profRes = await fetch(`${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(currentUserEmail)}`);
        const profData = await profRes.json();
        if (profData?.status === 'success' && profData?.data?.sub_city) {
          subCity = profData.data.sub_city;
          setOpdSubCity(subCity);
        } else {
          setMembers([]);
          setLoading(false);
          return;
        }
      }
      // Fetch all members from getAllMembers.php
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const res = await fetch(`${API_BASE_URL}/getAllMembers.php?${params.toString()}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      // Filter by sub city
      const norm = (s) => (s || '').toString().trim().toLowerCase();
      const subCityNorm = norm(subCity);
      const filtered = list.filter((m) => norm(m.subCity) === subCityNorm);
      setMembers(filtered);
    } catch (e) {
      setMembers([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchMembers(); /* eslint-disable-next-line */ }, []);

  // Reset to first page on filter/search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filter, daysLeft]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter(m => {
      const name = `${m.firstName || ''} ${m.middleName || ''} ${m.lastName || ''}`.toLowerCase();
      const idMatch = ((m.uniqueID || m.idNumber || '') + '').toLowerCase();
      if (q && !name.includes(q) && !idMatch.includes(q)) return false;
      const state = (m.membership_state || '').toLowerCase();
      if (filter === 'active' && state !== 'active') return false;
      if (filter === 'expired' && state !== 'expired') return false;
      if ((m.aaapd_status || '').toLowerCase() !== 'approved') return false;
      if (daysLeft !== "") {
        if (!m.membership_expires_at) return false;
        const now = new Date();
        const exp = new Date(m.membership_expires_at);
        const diffDays = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
        if (!(diffDays <= Number(daysLeft))) return false;
      }
      return true;
    });
  }, [members, search, filter, daysLeft]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop().toLowerCase();
    return ['jpg','jpeg','png','gif','bmp','webp','svg'].includes(ext);
  };

  const daysUntil = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const exp = new Date(expiresAt);
    const diff = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const handleDetail = (member) => { setDetailMember(member); setShowDetails(true); };

  const onFormClose = () => {
    setShowForm(false);
    setEditingMember(null);
  };

  const onMemberAdded = () => {
    fetchMembers();
    setEditingMember(null);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setShowForm(true);
  };

  const renewMember = async (member) => {
    if (!currentUserEmail) return alert('Please log in again.');
    if (!window.confirm(`Renew membership for ${member.firstName} ${member.lastName}?`)) return;
    try {
      setSubmittingId(member.id);
      const res = await fetch(`${API_BASE_URL}/updateMember.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: member.id, renew: true, requestEmail: currentUserEmail })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === 'error') throw new Error(data.message || 'Failed to renew');
      await fetchMembers();
      alert('Membership renewed successfully');
    } catch (e) {
      alert(e.message || 'Failed to renew');
    } finally {
      setSubmittingId(null);
    }
  };

  const exportCSV = () => {
    if (filtered.length === 0) return alert('No rows to export');
    const headers = ['Full Name','Unique ID','AAAPD Status','Membership State','Approved At','Expires At','Last Renewed At','Renewal Count','Days Remaining'];
    const toCell = (v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
    const rows = filtered.map(m => [
      `${[m.firstName, m.middleName, m.lastName].filter(Boolean).join(' ')}`,
      m.uniqueID || m.idNumber || '-',
      m.aaapd_status || '-',
      m.membership_state || '-',
      m.aaapd_approved_at || '-',
      m.membership_expires_at || '-',
      m.last_renewed_at || '-',
      m.renewal_count ?? '-',
      daysUntil(m.membership_expires_at) ?? '-'
    ]);
    const csv = [headers, ...rows].map(r => r.map(toCell).join(',')).join('\n');
    const BOM = "\uFEFF";
    const blob = new Blob([BOM, csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opd_renewals_${new Date().toISOString().replace(/[:.]/g,'-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Membership Renewals</h1>
          <p className="text-sm text-gray-500 mt-1">View expiry dates and renew your OPD members.</p>
        </header>

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Search by name or ID"
                value={search}
                onChange={(e)=>setSearch(e.target.value)}
              />
            </div>
            <select
              value={filter}
              onChange={(e)=>setFilter(e.target.value)}
              className="w-full md:w-48 border rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="all">All (Approved)</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                inputMode="numeric"
                className="w-40 border rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Days left ≤"
                value={daysLeft}
                onChange={(e)=>setDaysLeft(e.target.value)}
                title="Show members whose expiry date is within the specified number of days"
              />
              {daysLeft !== "" && (
                <button type="button" onClick={()=>setDaysLeft("")} className="text-gray-600 hover:text-gray-800 text-sm" title="Clear days filter">Clear</button>
              )}
            </div>
            <button onClick={exportCSV} disabled={filtered.length===0} className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-12 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading renewals...
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center p-12 text-gray-500">
              <AlertCircle className="h-5 w-5 mr-2" /> No members found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600 text-sm uppercase tracking-wider w-12">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Full Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">ID Number</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Approved</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Expires</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Days</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Renewals</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginated.map((m, index) => {
                    const state = (m.membership_state || '').toLowerCase();
                    const days = daysUntil(m.membership_expires_at);
                    const badgeCls = state === 'active' ? 'bg-green-100 text-green-800' : state === 'expired' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800';
                    return (
                      <tr key={m.id} className="hover:bg-gray-50">
                        <td className="py-4 px-4 whitespace-nowrap text-gray-600">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td className="px-4 py-3">{`${m.firstName || ''} ${m.middleName ? m.middleName + ' ' : ''}${m.lastName || ''}`}</td>
                        <td className="px-4 py-3">{m.uniqueID || m.idNumber || '-'}</td>
                        <td className="px-4 py-3"><span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeCls}`}>{m.membership_state || '-'}</span></td>
                        <td className="px-4 py-3">{m.aaapd_approved_at ? new Date(m.aaapd_approved_at).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3">{m.membership_expires_at ? new Date(m.membership_expires_at).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3">{typeof days === 'number' ? days : '-'}</td>
                        <td className="px-4 py-3">{m.renewal_count ?? 0}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-3">
                            <button onClick={() => { setDetailMember(m); setShowDetails(true); }} className="text-teal-600 hover:text-teal-700 font-medium" title="View Details">Details</button>
                            <button onClick={() => handleEdit(m)} className="text-gray-500 hover:text-blue-600" title="Edit Member">
                              <FilePenLine size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex flex-col md:flex-row justify-between items-center p-4 gap-4">
          <span className="text-sm text-gray-600">Showing {paginated.length} of {filtered.length} members</span>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
            <button
              className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>

        {/* Attachment Lightbox */}
        {attachmentPreview && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
            <div className="relative max-w-5xl max-h-[90vh] w-auto m-4">
              <button type="button" className="absolute -top-3 -right-3 bg-white text-gray-700 rounded-full shadow p-2 hover:bg-gray-100" onClick={() => setAttachmentPreview(null)} aria-label="Close attachment preview">
                <X size={20} />
              </button>
              <img src={attachmentPreview} alt="Attachment Preview" className="max-h-[85vh] max-w-full rounded-lg shadow-2xl" />
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showDetails && detailMember && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" aria-modal="true" role="dialog">
            <div className="relative bg-gray-100 rounded-xl shadow-2xl w-full max-w-3xl m-4 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-5 border-b border-gray-200 rounded-t-xl bg-white">
                <h3 className="text-xl font-semibold text-gray-800">Member Details</h3>
                <button type="button" className="p-2 text-gray-400 rounded-lg hover:bg-gray-200 hover:text-gray-900" onClick={() => { setShowDetails(false); setDetailMember(null); }} aria-label="Close details">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 flex-grow overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    { label: "Full Name", value: `${detailMember.firstName} ${detailMember.middleName || ""} ${detailMember.lastName}` },
                    { label: "Gender", value: detailMember.gender },
                    { label: "Date of Birth", value: detailMember.dateOfBirth },
                    { label: "Age", value: detailMember.age },
                    { label: "Phone Number", value: detailMember.phoneNumber },
                    { label: "Alternative Phone Number", value: detailMember.AltPhoneNumber },
                    { label: "Email", value: detailMember.email },
                    { label: "Location", value: detailMember.location },
                    { label: "Sub City", value: detailMember.subCity },
                    { label: "Woreda", value: detailMember.woreda },
                    { label: "House Number", value: detailMember.houseNumber },
                    { label: "Disability Type", value: detailMember.disabilityType === "Other" ? detailMember.disabilityOther || "Other" : detailMember.disabilityType },
                    { label: "Member Status", value: detailMember.aaapd_status },
                    { label: "Unique ID", value: detailMember.uniqueID || '-' },
                    { label: "Approved At", value: detailMember.aaapd_approved_at || '-' },
                    { label: "Expires At", value: detailMember.membership_expires_at || '-' },
                    { label: "Last Renewed At", value: detailMember.last_renewed_at || '-' },
                    { label: "Renewal Count", value: detailMember.renewal_count ?? '-' },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-sm font-medium text-gray-500">{label}</p>
                      {label === "Member Status" ? (
                        (() => {
                          const v = (value || "").toString();
                          const s = v.toLowerCase();
                          const cls = s === "approved" ? "bg-green-100 text-green-800" : s === "rejected" ? "bg-red-100 text-red-800" : s === "pending" ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-800";
                          return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>{v || "-"}</span>;
                        })()
                      ) : (
                        <p className="text-base text-gray-800 font-semibold">{value || "-"}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Attachments */}
                {detailMember.idAttachment && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-500 mb-2">Receipt Attachment</p>
                    {isImageFile(detailMember.idAttachment) ? (
                      <button type="button" className="group" onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.idAttachment}`)} title="Click to enlarge">
                        <img src={`${ATTACH_BASE_URL}/${detailMember.idAttachment}`} alt="Receipt Attachment" className="max-h-64 rounded-lg border border-gray-200 shadow-sm group-hover:opacity-90" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      </button>
                    ) : (
                      <a href={`${ATTACH_BASE_URL}/${detailMember.idAttachment}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">View/Download Attachment</a>
                    )}
                  </div>
                )}
                {detailMember.photoAttachment && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-500 mb-2">Passport Photo</p>
                    {isImageFile(detailMember.photoAttachment) ? (
                      <button type="button" className="group" onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`)} title="Click to enlarge">
                        <img src={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`} alt="Passport Photo" className="max-h-64 rounded-lg border border-gray-200 shadow-sm group-hover:opacity-90" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      </button>
                    ) : (
                      <a href={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">View/Download Photo</a>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-white rounded-b-xl">
                <button type="button" onClick={() => { setShowDetails(false); setDetailMember(null); }} className="px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300">Close</button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 text-xs text-gray-500 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-teal-600" /> Renewal extends from current expiry if still active.
        </div>
      {/* Add/Edit Member Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out" aria-modal="true" role="dialog">
          <div className="relative bg-gray-100 rounded-xl shadow-2xl w-full max-w-2xl m-4 max-h-[90vh] flex flex-col animate-fade-in-down">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 rounded-t-xl bg-white">
              <h3 id="member-form-title" className="text-xl font-semibold text-gray-800">
                {editingMember ? 'Edit Member' : 'Add Member'}
              </h3>
              <button
                type="button"
                className="p-2 text-gray-400 bg-transparent rounded-lg hover:bg-gray-200 hover:text-gray-900"
                onClick={onFormClose}
                aria-label="Close form"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              {(() => {
                const currentUser = JSON.parse(localStorage.getItem("currentUser"));
                const currentUserEmail = currentUser?.email || "";
                return (
                  <MemberForm editingMember={editingMember} currentUserEmail={currentUserEmail} onMemberAdded={onMemberAdded} onClose={onFormClose} />
                );
              })()}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
